import cn from "classnames";
import { useEffect, useCallback, useState, useRef } from "react";

// components
import { Button, Textarea } from "components/LibraryComponents/Components";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// utils
import { saveErrorToast } from "utils/edit-profile";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// styles
import styles from "../ServiceSection.module.scss";

import { updateExtendedProfileData } from "data/fetch";

const MAX_EQUIPMENT_CHARACTERS = 500;

type Props = {
  setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  saveConfirmation: ReturnType<typeof useSaveConfirmation>;
  section: string;
};

export default function SpecialEquipmentSection({ setUnsavedChanges, saveConfirmation, section }: Props) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [equipment, setEquipment] = useState<string>("");
  const [showEquipmentLimitError, setShowEquipmentLimitError] =
    useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // const SPECIAL_EQUIPMENT_MATERIALS = "Special equipment/materials" as const;

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = saveConfirmation

  const addTask = useMeiliTaskToast();

  const isInitialized = useRef(false); // Track if equipment has been initialized

  // Check if equipment has changed
  const hasFieldChanged = useCallback(() => equipment !== extendedProfile?.special_equipment, [equipment, extendedProfile]);

  // Update unsaved changes state
  useEffect(() => {
    setUnsavedChanges(hasFieldChanged());
    clearConfirmation();
    return () => {
      setUnsavedChanges(false);
    };
  }, [hasFieldChanged, setUnsavedChanges]);

  // Initialize equipment from extendedProfile
  useEffect(() => {
    if (extendedProfile && !isInitialized.current && equipment === "") {
      setEquipment(extendedProfile.special_equipment ?? "");
      isInitialized.current = true;
    }
  }, [extendedProfile]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= MAX_EQUIPMENT_CHARACTERS) {
      setEquipment(e.target.value);

      if (showEquipmentLimitError) {
        setShowEquipmentLimitError(false);
      }
    } else {
      setShowEquipmentLimitError(true);
    }
  };

  const handleSaveField = async () => {
    if (!hasFieldChanged() || isSaving || !equipment) return;

    setIsSaving(true);
    try {
      const response =await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          special_equipment: equipment,
        })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        special_equipment: equipment,
      });

      const responseData = await response.json();
      addTask({
        taskUid: responseData?.taskUid,
        label: section,
        status: responseData?.status,
      })
      showConfirmation(section);
    } catch (error: any) {
      console.error(`Error saving special equipment:`, error);
      saveErrorToast();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn(styles["section-content"], styles["space-y-6"])}>
      <div>
        <h3 className={styles["title"]}>Special Equipment/Materials</h3>
        <p className={styles["desc"]}>
          Describe any special equipment or materials your small business uses
          in a short paragraph.
        </p>
      </div>

      <div className={styles["text-area-wrapper"]}>
        <Textarea
          id="equipment"
          name="equipment"
          label="Special Equipment/Materials"
          placeholder="Type your capabilities narrative here"
          value={equipment}
          onChange={handleChange}
          inputClassName={
            showEquipmentLimitError ? styles["se-limit-error"] : ""
          }
        />

        <span className={styles["se-limit-text"]}>
          {equipment.length}/{MAX_EQUIPMENT_CHARACTERS}
        </span>

        {showEquipmentLimitError && (
          <span className={styles["se-limit-error-text"]}>
            Special equipment/materials must be under 500 characters.
          </span>
        )}
      </div>

      <ResponsiveStack>
        <Button
          type="button"
          loading={isSaving}
          disabled={!hasFieldChanged() || isSaving || !equipment}
          onClick={handleSaveField}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
        {lastSavedSection === section && <Saved />}
      </ResponsiveStack>
    </div>
  );
}
